import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './BulkUploadPage.css';
import ReactTable from '../../components/ReactTable';
import Loader from '../../components/Loader';
import Toast from '../../components/ToastNotification';
import {
    useUploadBulkCSVMutation,
    useListBatchesMutation,
    useGetSampleCSVMutation
} from './api/bulkUploadApi';
import { formatedDateAndTime } from '../../utils/helpers';
import { DEFAULT_PAGE_SIZE } from '../../utils/contants';
import {
    BATCH_STATUS_LABELS,
    BATCH_STATUS_COLORS,
    ATTACHMENT_ACCEPT,
    ATTACHMENT_MAX_SIZE,
    ATTACHMENT_TYPE_LABELS,
    getAttachmentType,
    isAllowedAttachment,
    isVideoConvertedToMp4,
    formatFileSize,
    downloadCSVString
} from './utils/bulkUploadHelpers';

// ─── Upload Form ──────────────────────────────────────────────────────────────

const UploadForm = ({ onUploadSuccess }) => {
    const [csvFile, setCsvFile] = useState(null);
    const [attachment, setAttachment] = useState(null);
    const [message, setMessage] = useState('');
    const [errors, setErrors] = useState({});
    const [uploadResult, setUploadResult] = useState(null);

    const [uploadBulkCSVApi, { isLoading: isUploading }] = useUploadBulkCSVMutation();
    const [getSampleCSVApi, { isLoading: isSampleLoading }] = useGetSampleCSVMutation();

    const validate = () => {
        const newErrors = {};
        if (!csvFile) newErrors.csvFile = 'Please select a CSV file.';
        if (!message.trim()) newErrors.message = 'Message template is required.';
        return newErrors;
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        if (!file.name.toLowerCase().endsWith('.csv')) {
            setErrors((prev) => ({ ...prev, csvFile: 'Only .csv files are allowed.' }));
            setCsvFile(null);
            e.target.value = '';
            return;
        }
        setCsvFile(file);
        setErrors((prev) => ({ ...prev, csvFile: undefined }));
        setUploadResult(null);
    };

    const clearAttachmentInput = () => {
        const input = document.getElementById('bulk-attachment-input');
        if (input) input.value = '';
    };

    const handleAttachmentChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        let error;
        if (!isAllowedAttachment(file)) {
            error = 'Attachment must be an image, video, audio or document file.';
        } else if (file.size > ATTACHMENT_MAX_SIZE) {
            error = 'Attachment must be 100 MB or smaller.';
        }

        if (error) {
            setErrors((prev) => ({ ...prev, attachment: error }));
            setAttachment(null);
            clearAttachmentInput();
            return;
        }

        setAttachment(file);
        setErrors((prev) => ({ ...prev, attachment: undefined }));
        setUploadResult(null);
    };

    const removeAttachment = () => {
        setAttachment(null);
        setErrors((prev) => ({ ...prev, attachment: undefined }));
        clearAttachmentInput();
    };

    const attachmentType = attachment ? getAttachmentType(attachment.type) : null;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setUploadResult(null);

        const validationErrors = validate();
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }
        setErrors({});

        const formData = new FormData();
        formData.append('csv', csvFile);
        formData.append('message', message.trim());
        if (attachment) formData.append('attachment', attachment);

        try {
            const response = await uploadBulkCSVApi(formData).unwrap();
            const result = response?.data;
            setUploadResult(result);

            if (result?.batch_id) {
                Toast.success('Bulk upload processed successfully.');
                setCsvFile(null);
                setMessage('');
                setAttachment(null);
                document.getElementById('bulk-csv-input').value = '';
                clearAttachmentInput();
                onUploadSuccess?.();
            } else {
                Toast.warn(result?.message || 'No authenticated rows found. Batch was not created.');
            }
        } catch (error) {
            Toast.error(error?.data?.message || 'Upload failed. Please try again.');
        }
    };

    const handleDownloadSample = async () => {
        try {
            const csvText = await getSampleCSVApi().unwrap();
            downloadCSVString(csvText, 'bulk_upload_sample.csv');
        } catch (error) {
            Toast.error('Failed to download sample CSV.');
        }
    };

    const isNoAuth = uploadResult && uploadResult.authenticated_count === 0;

    return (
        <div className="bulk-upload-card">
            <div className="layout-header">
                <h2>Upload Bulk Messages</h2>
            </div>
            <hr />

            <form className="bulk-upload-form" onSubmit={handleSubmit} noValidate>
                <div className="bulk-form-row">
                    {/* CSV File */}
                    <div className="bulk-form-group">
                        <label htmlFor="bulk-csv-input">CSV File <span style={{ color: '#ef4444' }}>*</span></label>
                        <input
                            id="bulk-csv-input"
                            type="file"
                            accept=".csv"
                            onChange={handleFileChange}
                            className={errors.csvFile ? 'input-error' : ''}
                        />
                        {errors.csvFile && <span className="bulk-field-error">{errors.csvFile}</span>}
                        <span className="bulk-helper-text">
                            Required columns: <code>from_number</code>, <code>client_name</code>, <code>client_number</code>
                        </span>
                    </div>

                    {/* Message Template */}
                    <div className="bulk-form-group">
                        <label htmlFor="bulk-message-input">
                            Message Template <span style={{ color: '#ef4444' }}>*</span>
                        </label>
                        <textarea
                            id="bulk-message-input"
                            rows={5}
                            placeholder="Hi {{client_name}}, this is {{employee_name}}..."
                            value={message}
                            onChange={(e) => {
                                setMessage(e.target.value);
                                setErrors((prev) => ({ ...prev, message: undefined }));
                            }}
                            className={errors.message ? 'input-error' : ''}
                        />
                        {errors.message && <span className="bulk-field-error">{errors.message}</span>}
                        <span className="bulk-helper-text">
                            Supported variables: <code>{'{{client_name}}'}</code>, <code>{'{{employee_name}}'}</code>
                        </span>

                        {/* Attachment (optional) */}
                        <div className="bulk-attachment-field">
                            <label htmlFor="bulk-attachment-input">
                                Attachment <span className="bulk-optional">(optional)</span>
                            </label>
                            {attachment ? (
                                <div className="bulk-attachment-chip">
                                    <span className="bulk-attachment-type">
                                        {ATTACHMENT_TYPE_LABELS[attachmentType]}
                                    </span>
                                    <span className="bulk-attachment-name" title={attachment.name}>
                                        {attachment.name}
                                    </span>
                                    <span className="bulk-attachment-size">{formatFileSize(attachment.size)}</span>
                                    <button
                                        type="button"
                                        className="bulk-attachment-remove"
                                        onClick={removeAttachment}
                                        aria-label="Remove attachment"
                                    >
                                        Remove
                                    </button>
                                </div>
                            ) : null}
                            <input
                                id="bulk-attachment-input"
                                type="file"
                                accept={ATTACHMENT_ACCEPT}
                                onChange={handleAttachmentChange}
                                className={errors.attachment ? 'input-error' : ''}
                                hidden={Boolean(attachment)}
                            />
                            {errors.attachment && <span className="bulk-field-error">{errors.attachment}</span>}
                            <span className="bulk-helper-text">
                                {attachmentType === 'audio'
                                    ? 'Audio can\'t have a caption, so the message is sent as a separate text after the audio.'
                                    : attachment && isVideoConvertedToMp4(attachment)
                                        ? 'WhatsApp only plays MP4 videos, so this video is converted to MP4 before sending. Large videos can take a few minutes.'
                                        : 'Image, video, audio or document, up to 100 MB. The message is sent as its caption.'}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Upload result banner */}
                {uploadResult && (
                    <div className={`bulk-upload-result${isNoAuth ? ' bulk-upload-result--warn' : ''}`}>
                        <span>{uploadResult.message}</span>
                        <span className={`bulk-result-pill${isNoAuth ? ' bulk-result-pill--warn' : ''}`}>
                            Total: {uploadResult.total_records}
                        </span>
                        <span className={`bulk-result-pill${isNoAuth ? ' bulk-result-pill--warn' : ''}`}>
                            Authenticated: {uploadResult.authenticated_count}
                        </span>
                        <span className={`bulk-result-pill${isNoAuth ? ' bulk-result-pill--warn' : ''}`}>
                            Unauthenticated: {uploadResult.unauthenticated_count}
                        </span>
                    </div>
                )}

                <div className="bulk-form-actions">
                    <button
                        type="submit"
                        className="bulk-upload-btn"
                        disabled={isUploading}
                    >
                        {isUploading ? 'Uploading…' : 'Upload'}
                    </button>
                    <button
                        type="button"
                        className="bulk-sample-btn"
                        onClick={handleDownloadSample}
                        disabled={isSampleLoading}
                    >
                        {isSampleLoading ? 'Downloading…' : 'Download Sample CSV'}
                    </button>
                </div>
            </form>
        </div>
    );
};

// ─── Batch Listing ────────────────────────────────────────────────────────────

const BulkUploadPage = () => {
    const navigate = useNavigate();
    const [batches, setBatches] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [showLoader, setShowLoader] = useState(false);
    const [pagination, setPagination] = useState({
        pageIndex: 0,
        pageSize: DEFAULT_PAGE_SIZE
    });

    const [listBatchesApi] = useListBatchesMutation();

    const fetchBatches = async (pageIndex = 0) => {
        setShowLoader(true);
        try {
            const response = await listBatchesApi({
                page: pageIndex + 1,
                limit: DEFAULT_PAGE_SIZE
            }).unwrap();
            const data = response?.data;
            setBatches(data?.results ?? []);
            setTotalRecords(data?.total ?? 0);
        } catch (error) {
            Toast.error(error?.data?.message || 'Failed to load batches.');
            setBatches([]);
        } finally {
            setShowLoader(false);
        }
    };

    useEffect(() => {
        fetchBatches(pagination.pageIndex);
    }, [pagination.pageIndex]);

    const columns = useMemo(() => [
        {
            header: 'File Name',
            accessorKey: 'file_name',
            cell: ({ getValue }) => (
                <span title={getValue()} style={{ maxWidth: 180, display: 'inline-block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {getValue()}
                </span>
            )
        },
        {
            header: 'Message',
            accessorKey: 'original_message',
            cell: ({ getValue }) => {
                const val = getValue() || '';
                return (
                    <span title={val}>
                        {val.length > 50 ? val.slice(0, 50) + '…' : val}
                    </span>
                );
            }
        },
        {
            header: 'Attachment',
            accessorKey: 'attachment_name',
            cell: ({ row }) => {
                const { attachment_name, attachment_type } = row.original;
                if (!attachment_name) return '—';
                return (
                    <span title={attachment_name} style={{ maxWidth: 160, display: 'inline-block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {ATTACHMENT_TYPE_LABELS[attachment_type] || 'File'}: {attachment_name}
                    </span>
                );
            }
        },
        {
            header: 'Upload Date',
            accessorKey: 'requested_at',
            cell: ({ getValue }) => formatedDateAndTime(getValue()) || '—'
        },
        {
            header: 'Total',
            accessorKey: 'total_records'
        },
        {
            header: 'Authenticated',
            accessorKey: 'authenticated_count',
            cell: ({ getValue }) => (
                <span style={{ color: '#166534', fontWeight: 600 }}>{getValue()}</span>
            )
        },
        {
            header: 'Unauthenticated',
            accessorKey: 'unauthenticated_count',
            cell: ({ getValue }) => (
                <span style={{ color: '#991b1b', fontWeight: 600 }}>{getValue()}</span>
            )
        },
        {
            header: 'Status',
            accessorKey: 'status',
            cell: ({ getValue }) => {
                const status = getValue();
                return (
                    <span
                        className="bulk-status-badge"
                        style={{ backgroundColor: BATCH_STATUS_COLORS[status] || '#94a3b8' }}
                    >
                        {BATCH_STATUS_LABELS[status] || status}
                    </span>
                );
            }
        }
    ], []);

    const actionMenu = useMemo(() => [
        {
            icon: <button className="bulk-view-btn">View Report</button>,
            action: (row) => navigate(`/bulk-upload/${row.original.id}/report`)
        }
    ], [navigate]);

    const pageCount = Math.ceil(totalRecords / DEFAULT_PAGE_SIZE);

    return (
        <div className="bulk-upload-page">
            {/* Upload Form Section */}
            <UploadForm onUploadSuccess={() => fetchBatches(0)} />

            {/* Batch Listing Section */}
            <div className="bulk-upload-card">
                <div className="layout-header">
                    <h2>Upload History</h2>
                </div>
                <hr />

                <div className="layout-body" style={{ position: 'relative' }}>
                    {showLoader && <Loader />}

                    {!showLoader && batches.length === 0 ? (
                        <div className="bulk-empty-state">
                            <img src="/upload.png" alt="" style={{ width: 48, opacity: 0.35 }} />
                            <p>No uploads yet. Upload a CSV file to get started.</p>
                        </div>
                    ) : (
                        <ReactTable
                            columns={columns}
                            data={batches}
                            pageCount={pageCount}
                            paginationState={{ pagination, setPagination }}
                            actionMenu={actionMenu}
                            setSelectedRow={() => {}}
                            totalRecords={totalRecords}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default BulkUploadPage;
